import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import { computed, makeObservable, reaction } from 'mobx'
import { getValue, isPending } from 'mobx-async'

import { CaseRecordType } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { getTaxonomyTasks } from '@avantstay/case-taxonomy'

import * as arriere from '@new/domains/operations/screens/CasesV2/service/cases'
import {
  filterOptionsByRegions,
  filterOwnerOptionsByRegions,
} from '@new/domains/operations/screens/CasesV2/utils/filterOptionsByRegions'

import { arriere as globalArriere } from '@arriere/index'
import { ROUTES } from '@routes'
import { RoutingStore } from '@stores/RoutingStore'

import { casesFiltersStore } from './CasesFiltersStore'

class _CaseFilterOptionsStore {
  constructor() {
    makeObservable<
      _CaseFilterOptionsStore,
      | 'getOwnersPromise'
      | 'getVendorsPromise'
      | 'getCreatorsPromise'
      | 'getPropertiesPromise'
      | 'getTaxonomyTasksPromise'
    >(this, {
      getTaxonomyTasksPromise: computed,
      taxonomyTasks: computed({ keepAlive: true }),
      isLoadingTaxonomyTasks: computed,
      getOwnersPromise: computed,
      ownersByRecordTypes: computed({ keepAlive: true }),
      isLoadingCaseOwners: computed,
      ownersInSelectedRegions: computed,
      staffEmailsPromise: computed,
      getVendorsPromise: computed,
      vendorsByRecordTypes: computed({ keepAlive: true }),
      vendorsInSelectedRegions: computed,
      isLoadingCaseVendors: computed,
      getCreatorsPromise: computed,
      creatorsByRecordTypes: computed({ keepAlive: true }),
      isLoadingCaseCreators: computed,
      getPropertiesPromise: computed,
      propertiesOptions: computed({ keepAlive: true }),
      isLoadingProperties: computed,
    })

    this.setupRouteReaction()
  }

  private setupRouteReaction() {
    reaction(
      () => RoutingStore?.location?.pathname,
      pathname => {
        if (this.shouldInitializeFilters(pathname)) {
          this.initializeFilters()
        }
      },
    )
  }

  private shouldInitializeFilters(pathname?: string): boolean {
    return !!pathname && pathname.includes(ROUTES.sfdcWorkOrder.path)
  }

  private initializeFilters() {
    getValue(this.getOwnersPromise)
    getValue(this.getVendorsPromise)
    getValue(this.getCreatorsPromise)
  }

  private get getTaxonomyTasksPromise() {
    return getTaxonomyTasks()
  }

  get taxonomyTasks() {
    return getValue(this.getTaxonomyTasksPromise) ?? []
  }

  get isLoadingTaxonomyTasks() {
    return isPending(this.getTaxonomyTasksPromise)
  }

  private get getOwnersPromise() {
    return arriere.getOwners(Object.values(CaseRecordType))
  }

  get ownersByRecordTypes() {
    return getValue(this.getOwnersPromise) ?? []
  }

  /**
   * Roster emails for the selected markets — the primary scoping signal for
   * the Case Owner options. Undefined while loading, null when the fetch
   * failed; both degrade to the conservative vendor-style scoping.
   */
  get staffEmailsPromise() {
    const regions = casesFiltersStore.regions
    if (!regions?.length) return Promise.resolve(null)
    return arriere.getStaffEmailsForRegions([...regions].sort())
  }

  /**
   * Case Owner options for the checkbox filter. `ownersByRecordTypes` stays
   * the unscoped list — the preview formatters resolve id→name from it and
   * must still recognise a selection made under another market.
   */
  get ownersInSelectedRegions() {
    return filterOwnerOptionsByRegions(
      this.ownersByRecordTypes,
      casesFiltersStore.regions,
      casesFiltersStore.caseOwners,
      getValue(this.staffEmailsPromise),
    )
  }

  get isLoadingCaseOwners() {
    return isPending(this.getOwnersPromise)
  }

  private get getVendorsPromise() {
    return arriere.getVendors(Object.values(CaseRecordType))
  }

  get vendorsByRecordTypes() {
    return getValue(this.getVendorsPromise) ?? []
  }

  /** Case Vendor options for the checkbox filter — see ownersInSelectedRegions. */
  get vendorsInSelectedRegions() {
    return filterOptionsByRegions(
      this.vendorsByRecordTypes,
      casesFiltersStore.regions,
      casesFiltersStore.caseVendors,
    )
  }

  get isLoadingCaseVendors() {
    return isPending(this.getVendorsPromise)
  }

  private get getCreatorsPromise() {
    return arriere.getCreators(Object.values(CaseRecordType))
  }

  get creatorsByRecordTypes() {
    return getValue(this.getCreatorsPromise) ?? []
  }

  get isLoadingCaseCreators() {
    return isPending(this.getCreatorsPromise)
  }

  private get getPropertiesPromise() {
    return globalArriere.properties
      .getHomeOptions(undefined, casesFiltersStore.regions)
      .then(result => {
        const groupedList = groupBy(sortBy(result, 'name'), 'region.name')
        return result.map(property => ({
          value: property.id,
          label: property.name,
          group: `${property.region?.name} • ${groupedList[property.region?.name ?? ''].length}`,
          hasFullWidth: true,
        }))
      })
  }

  get propertiesOptions() {
    return getValue(this.getPropertiesPromise) ?? []
  }

  get isLoadingProperties() {
    return isPending(this.getPropertiesPromise)
  }
}

export const casesFilterOptionsStore = new _CaseFilterOptionsStore()
